import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AssetType } from '@k-ucap/shared';

export interface FavoriteItem {
  id: string;
  assetType: AssetType;
  assetId: string;
  name: string;
  projectId: string;
  href: string;
  createdAt: string;
}

interface FavoriteState {
  items: Record<string, FavoriteItem>;
  add(item: Omit<FavoriteItem, 'createdAt'>): void;
  remove(key: string): void;
  toggle(item: Omit<FavoriteItem, 'createdAt'>): void;
  isFavorite(assetType: AssetType, assetId: string): boolean;
  getFavorites(): FavoriteItem[];
  getFavoritesByType(assetType: AssetType): FavoriteItem[];
  clear(): void;
}

const buildKey = (assetType: AssetType, assetId: string): string => `${assetType}:${assetId}`;

export const useFavoriteStore = create<FavoriteState>()(
  persist(
    (set, get) => ({
      items: {},
      add: (item) => {
        const key = buildKey(item.assetType, item.assetId);
        set((state) => ({
          items: {
            ...state.items,
            [key]: { ...item, createdAt: new Date().toISOString() },
          },
        }));
      },
      remove: (key) => {
        set((state) => {
          const next = { ...state.items };
          delete next[key];
          return { items: next };
        });
      },
      toggle: (item) => {
        const key = buildKey(item.assetType, item.assetId);
        const state = get();
        if (state.items[key]) {
          const next = { ...state.items };
          delete next[key];
          set({ items: next });
        } else {
          set({ items: { ...state.items, [key]: { ...item, createdAt: new Date().toISOString() } } });
        }
      },
      isFavorite: (assetType, assetId) => !!get().items[buildKey(assetType, assetId)],
      getFavorites: () => Object.values(get().items).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      getFavoritesByType: (assetType) =>
        Object.values(get().items)
          .filter((item) => item.assetType === assetType)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      clear: () => set({ items: {} }),
    }),
    {
      name: 'k-ucap-favorites',
      partialize: (state) => ({ items: state.items }),
    }
  )
);
